import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatINR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Building2, MapPin, CalendarDays, Crown, Users as UsersIcon, Briefcase, IndianRupee,
  TrendingUp, TrendingDown, AlertCircle,
} from "lucide-react";

export default function MyBranch() {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pnl, setPnl] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const branch = branches[0] || null;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/branches");
        if (cancelled) return;
        setBranches(r.data || []);
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.detail || "Failed to load branch");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!branch) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/branches/${branch.id}/pnl`, {
          params: { date_from: dateFrom || undefined, date_to: dateTo || undefined },
        });
        if (!cancelled) setPnl(r.data);
      } catch (err) { /* swallow */ void 0; }
    })();
    return () => { cancelled = true; };
  }, [branch, dateFrom, dateTo]);

  if (loading) return <div className="text-stone-500">Loading your branch…</div>;

  if (!branch) {
    return (
      <div className="bg-amber-50 border border-amber-300 rounded-2xl p-10 text-center max-w-xl" data-testid="my-branch-empty">
        <AlertCircle className="w-12 h-12 mx-auto text-amber-700" />
        <div className="mt-3 font-display text-xl font-black text-stone-900">No branch assigned</div>
        <p className="text-sm text-stone-700 mt-2">
          You&apos;re not currently heading a branch. Once you reach <b>Associate Circle Head (Level 4)</b>,
          your admin will open a branch and assign you as the head.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-branch-root">
      <div>
        <h1 className="font-display text-3xl font-black text-stone-900 flex items-center gap-2">
          <Building2 className="w-7 h-7 text-emerald-700" /> {branch.name}
        </h1>
        <div className="text-sm text-stone-600 mt-1 flex items-center gap-3 flex-wrap">
          {branch.city && <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {branch.city}</span>}
          {branch.opening_date && <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" /> Opened {branch.opening_date}</span>}
          <span className="text-[10px] uppercase font-bold tracking-wider rounded-full border px-2 py-0.5 bg-emerald-100 text-emerald-800 border-emerald-300">{branch.status}</span>
        </div>
      </div>

      <div className="bg-gradient-to-br from-emerald-900 to-emerald-700 rounded-3xl p-6 text-white" data-testid="my-branch-pnl">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="font-display text-xl font-black">Branch P&L</div>
          <div className="flex items-center gap-2 text-xs">
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-8 bg-white/95 text-stone-900 w-36" />
            <span className="text-white/70">→</span>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-8 bg-white/95 text-stone-900 w-36" />
          </div>
        </div>
        {pnl && (
          <>
            <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Revenue (reconciled)" value={pnl.revenue.amount} count={pnl.revenue.count} />
              <Stat label="Booked (paid)" value={pnl.booked.amount} count={pnl.booked.count} />
              <Stat label="Total Cost" value={pnl.cost.total} />
              <Stat label="Net" value={pnl.net} highlight />
            </div>
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
              <div className="bg-white/10 rounded-xl p-3">
                <div className="text-white/70">Setup cost</div>
                <div className="font-bold">{formatINR(pnl.cost.setup)}</div>
              </div>
              <div className="bg-white/10 rounded-xl p-3">
                <div className="text-white/70">Monthly costs ({pnl.range.months} mo)</div>
                <div className="font-bold">{formatINR(pnl.cost.monthly)}</div>
              </div>
              <div className="bg-white/10 rounded-xl p-3">
                <div className="text-white/70">Staff salaries</div>
                <div className="font-bold">{formatINR(pnl.cost.salaries)}</div>
              </div>
            </div>
          </>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="my-branch-partners">
          <div className="font-display text-base font-bold text-stone-900 flex items-center gap-2 mb-3">
            <UsersIcon className="w-5 h-5 text-emerald-700" /> Branch partners ({branch.partner_count})
          </div>
          <p className="text-xs text-stone-600">All consultants assigned to your branch. Their leads contribute to the branch P&L above.</p>
        </div>
        <div className="bg-white border border-stone-200 rounded-2xl p-5" data-testid="my-branch-staff">
          <div className="font-display text-base font-bold text-stone-900 flex items-center gap-2 mb-3">
            <Briefcase className="w-5 h-5 text-emerald-700" /> Salaried staff ({branch.staff_count})
          </div>
          {(branch.staff_members || []).length === 0 ? (
            <div className="text-xs text-stone-500 italic">No salaried staff yet.</div>
          ) : (
            <div className="space-y-1">
              {(branch.staff_members || []).map(s => (
                <div key={s.id} className="bg-stone-50 rounded-lg px-3 py-2 text-sm flex items-center justify-between">
                  <div>
                    <span className="font-semibold">{s.name}</span>
                    {s.role && <span className="text-stone-500 text-xs ml-2">{s.role}</span>}
                  </div>
                  <span className="text-stone-600 text-xs">{formatINR(s.monthly_salary || 0)}/mo</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, count, highlight }) {
  return (
    <div className={`rounded-2xl p-4 ${highlight ? `bg-gradient-to-br ${value >= 0 ? "from-emerald-400 to-emerald-600" : "from-rose-400 to-rose-600"} text-white` : "bg-white/10 text-white"}`}>
      <div className={`text-[11px] uppercase tracking-wider ${highlight ? "text-white/90" : "text-white/70"}`}>{label}</div>
      <div className="font-display text-2xl font-black mt-1 flex items-center gap-1">
        <IndianRupee className="w-4 h-4" />{Math.round(value || 0).toLocaleString("en-IN")}
      </div>
      {count !== null && count !== undefined && (
        <div className={`text-[11px] mt-0.5 ${highlight ? "text-white/90" : "text-white/70"}`}>{count} {count === 1 ? "entry" : "entries"}</div>
      )}
      {highlight && (
        <div className="mt-1 text-[11px] font-bold flex items-center gap-1">
          {value >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {value >= 0 ? "Profit" : "Loss"}
        </div>
      )}
    </div>
  );
}
